import React, { useCallback, useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Paper,
  Button,
  Stack,
  MenuItem,
  Select,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MUITypography from '../../../Components/MUITypography';
import Loader from '../../../Components/Loader';
import { API_CREATE_INVOICE_PAYMENT, API_GET_INVOICE_PAYMENT } from '../../../Util/URLConstants';
import axios from 'axios';
import { align_row, VM_Colors } from '../../../Styles/VMStyles';
import StyledInputField from '../../../Components/Custom/StyledInputField';
import MuiTypographyMandatory from '../../../Components/Custom/MuiTypographyMandatory';
import { formatAmount, formattedDateTime } from '../../../Util/UtilHelper';
import StorageFactory from '../../../Util/StorageFactory';
import { SESSION_USER_COUNTRY_CODE_KEY, SESSION_USER_COUNTRY_DATA_KEY, SESSION_USER_ID_KEY, STATUS_PENDING } from '../../../Util/VMConstants';
import { toast } from 'react-toastify';
import { convertToWords } from '../../../Util/NumberToWord';

const UpdatePaymentDrawer = ({ open, onClose, enquiry_id,invoice_id }) => {
  const [state, setState] = useState({
    loading: false,
    payments: [],
    invoice_payment: [],
  });

  const [summary, setSummary] = useState({
    total_amount: 0,
    advance_amount: 0,
    pending_amount: 0,
    status: STATUS_PENDING,
  });

  const [addPaymentMode, setAddPaymentMode] = useState(false);
  const [form, setForm] = useState({ amount: '', mode: '', customMode: '' });

  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const countryCode = sessionFactory.get(SESSION_USER_COUNTRY_CODE_KEY);
  const currencySymbol = sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || '₹';



  const paymentModes = ["Cash", "Card", "Online","UPI","Others"];

  const getInvoicePaymentInfo = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true }));

    axios
      .get(`${API_GET_INVOICE_PAYMENT}?invoice_id=${invoice_id}`)
      .then((resp) => {
        const InvInfo = resp?.data?.invoice_info || [];

        if (InvInfo.length > 0) {
          const { final_payable_amount, is_invoice_clear } = InvInfo[0];
          const payments = resp?.data?.invoice_payments || [];

          const totalPaid = payments.reduce((acc, p) => acc + Number(p.advance_amount), 0);
          const pending = Number(final_payable_amount) - totalPaid;

          setSummary({
            total_amount: final_payable_amount,
            advance_amount: totalPaid,
            pending_amount: pending,
            status: is_invoice_clear,
          });

          setState({ loading: false, payments, invoice_payment: InvInfo });
        } else {
          setState((prev) => ({ ...prev, loading: false }));
        }
      })
      .catch((err) => {
        console.error('Error fetching invoice payment info:', err);
        setState((prev) => ({ ...prev, loading: false }));
      });
  }, [enquiry_id]);

  useEffect(() => {
    if (enquiry_id) getInvoicePaymentInfo();
  }, [enquiry_id, getInvoicePaymentInfo]);

  const handleSubmit = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));
      const pA = Number(summary.pending_amount);
      const fA = Number(form.amount);
      if(pA < fA){
        toast.error("Amount should be less than Pending amount")
        return;
      }
      const pending = pA - fA ;

      const reqbody = {
        invoice_id: invoice_id,
        enquiry_id,
        total_amount  : summary.total_amount,
        advance_amount: Number(form.amount),
        payment_mode  : form.mode === 'Others' ? form.customMode : form.mode,
        pending_amount: pending,
        user_id       : userID
      };

      await axios.post(API_CREATE_INVOICE_PAYMENT, reqbody);
      setForm({ amount: '', mode: '', customMode: '' });
      setAddPaymentMode(false);
      getInvoicePaymentInfo();
    } catch (err) {
      console.error('Error submitting payment:', err);
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };


  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 420, p: 2, borderTopLeftRadius: 12, borderBottomLeftRadius: 12 },
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MUITypography variant="h3" fontWeight={600}>
          Payment History
        </MUITypography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Summary Section */}
     <Paper
        elevation={3}
        sx={{
          bgcolor: '#58b6c2',
          borderRadius: 2,
          p: 2,
          color: 'white',
          mb: 3,
          position: 'relative',
        }}
      >
        {/* Status Badge at Top Right */}
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            fontWeight: 600,
            fontSize: 13,
            bgcolor: 'white',
            color: summary.status === true ? VM_Colors.success : VM_Colors.error,
            boxShadow: 1,
          }}
        >
          {summary.status === true ? '✔ Payment Cleared' : '❌ Payment Pending'}
        </Box>

        {/* Summary Content */}
        <Stack spacing={1}>
          <Box sx={align_row}>
            <MUITypography variant='h5' sx={{width:100}}> <strong>Total Amount</strong> </MUITypography>
            <MUITypography > :  </MUITypography>
            <MUITypography sx={{ml:2}}> <strong>  { formatAmount(summary.total_amount, currencySymbol )} </strong>  </MUITypography>
          </Box>
          <Box sx={align_row}>
            <MUITypography variant='h5' sx={{width:100}}> <strong>Amount Paid</strong> </MUITypography>
            <MUITypography > :  </MUITypography>
            <MUITypography sx={{ml:2}}> <strong>  {formatAmount(summary.advance_amount,currencySymbol)} </strong>  </MUITypography>
          </Box>
          <Box sx={align_row}>
            <MUITypography variant='h5' sx={{width:100}}> <strong>Pending</strong> </MUITypography>
            <MUITypography > :  </MUITypography>
            <MUITypography sx={{ml:2}}> <strong>  { formatAmount(summary.pending_amount,currencySymbol)} </strong>  </MUITypography>
          </Box>

        </Stack>
      </Paper>


      {/* Payments List or Add Form */}
      {!addPaymentMode ? (
        <>
          <Stack spacing={2} sx={{ maxHeight: '55vh', overflowY: 'auto' }}>
            {state.payments.map((payment, index) => (
           <Paper
  key={payment.id}
  elevation={2}
  sx={{
    borderRadius: 2,
    p: 2,
    bgcolor: '#ffffff',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
  }}
>
  {/* Header */}
  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
    <MUITypography fontWeight={600} fontSize={15} color="text.primary">
      {index + 1}
      {['st', 'nd', 'rd'][index] || 'th'} Payment
    </MUITypography>
    <Box display="flex" alignItems="center" color="text.secondary">
      <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5 }} />
      <MUITypography variant="caption">{formattedDateTime(payment.created_date)}</MUITypography>
    </Box>
  </Box>

  {/* Details */}
  <Stack spacing={1} mt={1}>
    <Box display="flex" justifyContent="space-between">
      <MUITypography variant="body2" color="text.secondary">
        Amount Paid
      </MUITypography>
      <MUITypography variant="body2" fontWeight={600} color="#002e5f">
         {formatAmount(payment.advance_amount,currencySymbol)}
      </MUITypography>
    </Box>

    <Box display="flex" justifyContent="space-between">
      <MUITypography variant="body2" color="text.secondary">
        Payment Mode
      </MUITypography>
      <MUITypography variant="body2" fontWeight={600} color="#002e5f">
        {payment.payment_mode}
      </MUITypography>
    </Box>

    {(payment.created_by?.name || payment.created_by) && (
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <MUITypography variant="body2" color="text.secondary">
          Created by
        </MUITypography>
        <MUITypography variant="body2" fontWeight={600} color="#002e5f" sx={{ textAlign: 'right' }}>
          {typeof payment.created_by === 'object'
            ? [payment.created_by.name, payment.created_by.user_type || payment.created_by.userType]
                .filter(Boolean)
                .join(' ')
            : payment.created_by}
        </MUITypography>
      </Box>
    )}
  </Stack>
</Paper>

            ))}
          </Stack>

         {
          summary.status === false &&
          <Box mt={3} mb={5}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setAddPaymentMode(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                py: 1.2,
                bgcolor: '#1976d2',
                '&:hover': { bgcolor: '#115293' },
              }}
            >
              + Add Payment
            </Button>
          </Box>
        }   

        </>
      ) : (
        <Box mt={2}>
          <MUITypography fontWeight={600} mb={2}>
            Add Payment
          </MUITypography>

          <StyledInputField
            heading="Pending Amount"
            name="pending_amount"
            value={` ${ formatAmount(summary.pending_amount,currencySymbol) }`}
            InputProps={{ readOnly: true }}
            fullWidth
            margin="normal"
            style={{ mt: 1 }}
          />

          <StyledInputField
            heading="Amount"
            type="number"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            fullWidth
            margin="normal"
          />
           {
               form.amount>1 && countryCode === '91'
               ?  <MUITypography variant='subtitle1' label={ convertToWords(countryCode,form.amount)} sx={{color:VM_Colors.primary}} /> 
               : null
           }

          <Stack spacing={0.5} sx={{ minWidth: '200px', mt: 1 }}>
            <MuiTypographyMandatory label="Payment Mode" required />
            <Select
              value={form.mode}
              onChange={(e) => setForm({ ...form, mode: e.target.value })}
              sx={{ backgroundColor: VM_Colors.white }}
              fullWidth
              displayEmpty
            >
             <MenuItem value="" disabled>  -- Select --   </MenuItem>
              {paymentModes.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          {form.mode === 'Others' && (
            <StyledInputField
              heading="Enter Custom Payment Type"
              value={form.customMode}
              onChange={(e) => setForm({ ...form, customMode: e.target.value })}
              fullWidth
              margin="normal"
              style={{ mt: 1 }}
            />
          )}

          <Stack direction="row" spacing={2} mt={3}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setForm({ amount: '', mode: '', customMode: '' });
                setAddPaymentMode(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={
                !form.amount || !form.mode || (form.mode === 'Others' && !form.customMode)
              }
            >
              Submit
            </Button>
          </Stack>
        </Box>
      )}

      {state.loading && <Loader loading={state.loading} />}
    </Drawer>
  );
};

export default UpdatePaymentDrawer;
